import React, { useState } from 'react';
import { useLocale } from '../context/LocaleContext';

const AiDataProcessingConsentGate = ({
  onAgree,
  onDecline,
  isSubmitting = false,
  isDeleting = false,
  error = '',
}) => {
  const { t } = useLocale();
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [confirmDeclineOpen, setConfirmDeclineOpen] = useState(false);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#0B0F19] via-[#111827] to-[#0A192F] px-4 py-10 text-white">
      <div className="w-full max-w-2xl rounded-[2rem] border border-white/10 bg-slate-900/70 p-8 shadow-2xl backdrop-blur-2xl">
        <p className="text-xs font-bold uppercase tracking-[0.22em] text-cyan-300">{t('auth.consentTag', 'Data & Privacy')}</p>
        <h1 className="mt-2 text-3xl font-black text-white">{t('auth.consentTitle', 'AI Data Processing')}</h1>
        <p className="mt-5 leading-7 text-gray-200">
          {t('auth.consentIntro', 'Forza uses trusted AI and speech providers to generate personalized quests, coach you, verify completions, review exercise form, and power related AI features.')}
        </p>
        <p className="mt-4 leading-7 text-gray-300">
          {t('consentGate.summary', 'Your data may be sent to external providers. Consent is required to continue using Forza.')}
        </p>

        <div className="mt-6 flex flex-wrap gap-3 text-sm">
          <button
            type="button"
            onClick={() => setDetailsOpen((current) => !current)}
            className="rounded-full border border-white/10 bg-white/5 px-4 py-2 font-semibold text-cyan-300 hover:bg-white/10"
          >
            {detailsOpen ? t('consentGate.hideDetails', 'Hide details') : t('auth.viewDetails', 'View details')}
          </button>
          <a
            href="/privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-full border border-white/10 bg-white/5 px-4 py-2 font-semibold text-cyan-300 hover:bg-white/10"
          >
            {t('auth.privacyPolicy', 'Privacy Policy')}
          </a>
          <a
            href="/terms-of-service"
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-full border border-white/10 bg-white/5 px-4 py-2 font-semibold text-cyan-300 hover:bg-white/10"
          >
            {t('auth.termsOfService', 'Terms of Service')}
          </a>
        </div>

        {detailsOpen ? (
          <div className="mt-6 rounded-2xl border border-white/10 bg-slate-950/50 p-5 text-sm leading-6 text-gray-300">
            <p className="font-bold uppercase tracking-[0.16em] text-cyan-200">{t('auth.consentProcessed', 'Data that may be processed')}</p>
            <ul className="mt-3 list-disc space-y-2 pl-5">
              <li>{t('auth.consentProcessedItems.profile', 'Fitness goals, preferences, profile context, and onboarding answers.')}</li>
              <li>{t('auth.consentProcessedItems.quests', 'Quest plans, quest activity, completion status, and verification context.')}</li>
              <li>{t('auth.consentProcessedItems.text', 'Text you submit to AI-powered features.')}</li>
              <li>{t('auth.consentProcessedItems.voice', 'Voice recordings and transcripts submitted to Voice Coach or voice verification.')}</li>
              <li>{t('auth.consentProcessedItems.photos', 'Photos submitted for meal or quest verification.')}</li>
              <li>{t('auth.consentProcessedItems.exercise', 'Exercise frames submitted for form checks.')}</li>
              <li>{t('auth.consentProcessedItems.diagnostics', 'App, device, and diagnostic context needed to operate, secure, and troubleshoot these features.')}</li>
            </ul>

            <p className="mt-5 font-bold uppercase tracking-[0.16em] text-cyan-200">{t('auth.consentProviders', 'AI and speech providers')}</p>
            <p className="mt-3">
              {t('auth.consentProvidersBody', 'Forza may use Google, Groq, OpenRouter, DeepSeek, OpenAI, and local or on-device providers where available.')}
            </p>
            <p className="mt-3">
              {t('auth.consentInfrastructureBody', 'Related data may also be handled by providers used for hosting, storage, media delivery, monitoring, subscriptions, advertising consent and reward attribution, and security, including Render, MongoDB Atlas, Cloudflare R2, ImageKit, Better Stack, Apple, RevenueCat, and Google AdMob where relevant.')}
            </p>

            <p className="mt-5 font-bold uppercase tracking-[0.16em] text-cyan-200">{t('auth.consentWhenSent', 'When data is sent')}</p>
            <ul className="mt-3 list-disc space-y-2 pl-5">
              <li>{t('auth.consentWhenSentItems.onboarding', 'During onboarding and quest generation.')}</li>
              <li>{t('auth.consentWhenSentItems.coach', 'When you use AI Coach or Voice Coach.')}</li>
              <li>{t('auth.consentWhenSentItems.verification', 'When you submit voice notes, photos, or exercise frames for verification or form checks.')}</li>
              <li>{t('auth.consentWhenSentItems.planning', 'When AI-powered features need app context to respond, verify progress, or plan quests.')}</li>
            </ul>
          </div>
        ) : null}

        {confirmDeclineOpen ? (
          <div className="mt-6 rounded-2xl border border-rose-300/20 bg-rose-500/10 p-5 text-sm leading-6 text-rose-100">
            <p className="font-bold uppercase tracking-[0.16em] text-rose-200">{t('consentGate.declineConfirmation', 'Decline confirmation')}</p>
            <p className="mt-3">
              {t('consentGate.declineBody', 'If you decline AI data processing consent, your account data will be deleted or anonymized and you will be signed out.')}
            </p>
            <div className="mt-5 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => setConfirmDeclineOpen(false)}
                className="rounded-xl border border-white/15 px-5 py-3 font-bold text-gray-200 hover:bg-white/10"
              >
                {t('consentGate.goBack', 'Go back')}
              </button>
              <button
                type="button"
                onClick={onDecline}
                disabled={isDeleting}
                className="rounded-xl bg-rose-600 px-5 py-3 font-bold text-white hover:bg-rose-500 disabled:opacity-60"
              >
                {isDeleting ? t('consentGate.deleting', 'Deleting account...') : t('consentGate.declineDelete', 'Decline and delete account')}
              </button>
            </div>
          </div>
        ) : null}

        {error ? (
          <div className="mt-6 rounded-xl border border-red-900/50 bg-red-950/50 p-4 text-sm font-medium text-red-200">
            {error}
          </div>
        ) : null}

        <div className="mt-8 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          {!confirmDeclineOpen ? (
            <button
              type="button"
              onClick={() => setConfirmDeclineOpen(true)}
              disabled={isSubmitting || isDeleting}
              className="rounded-xl border border-white/15 px-5 py-3 font-bold text-gray-200 hover:bg-white/10 disabled:opacity-60"
            >
              {t('auth.consentDecline', 'Decline')}
            </button>
          ) : null}
          <button
            type="button"
            onClick={onAgree}
            disabled={isSubmitting || isDeleting}
            className="rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 px-5 py-3 font-bold text-white hover:from-cyan-400 hover:to-blue-500 disabled:opacity-60"
          >
            {isSubmitting ? t('consentGate.saving', 'Saving consent...') : t('auth.consentAgree', 'Agree and continue')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AiDataProcessingConsentGate;
